/**
 * This layer's own bookkeeping under `nightdrop.cloud.v1`: the code, the revision last seen,
 * what still has to be sent. Written on every change, because a tab can close at any moment and
 * the next start must know what the server has not been told.
 */

import { isValidCode, normaliseCode } from './saveCode';
import type { CloudKey, CloudState, KeyValueStore } from './types';
import { isCloudKey, STATE_KEY } from './types';

/** What a missing value fingerprints as: differs from every real value, the empty string included. */
export const NO_VALUE = 'none';

/**
 * Length plus 32-bit FNV-1a: enough to tell "the server's copy changed since we last agreed"
 * without keeping a second copy of a megabyte of suspend save. Not a security measure.
 */
export function fingerprint(value: string | null | undefined): string {
  if (value === null || value === undefined) return NO_VALUE;
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${value.length}:${(hash >>> 0).toString(16)}`;
}

export function freshState(code: string): CloudState {
  return { code, revision: 0, dirty: [], dirtyAt: null, syncedAt: null, base: {}, backups: [] };
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** The stored state, or null when there is none or it cannot be trusted (then a new code is made). */
export function loadState(storage: KeyValueStore): CloudState | null {
  let raw: unknown;
  try {
    raw = JSON.parse(storage.getItem(STATE_KEY) ?? 'null');
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r['code'] !== 'string' || !isValidCode(r['code'])) return null;
  const revision = numberOrNull(r['revision']);
  const listed: unknown[] = Array.isArray(r['dirty']) ? r['dirty'] : [];
  const dirty = listed.filter((k): k is CloudKey => typeof k === 'string' && isCloudKey(k));
  const base: Partial<Record<CloudKey, string>> = {};
  if (typeof r['base'] === 'object' && r['base'] !== null) {
    for (const [key, value] of Object.entries(r['base'])) {
      if (isCloudKey(key) && typeof value === 'string') base[key] = value;
    }
  }
  return {
    code: normaliseCode(r['code']),
    revision: revision !== null && revision >= 0 ? Math.floor(revision) : 0,
    dirty: [...new Set(dirty)],
    dirtyAt: numberOrNull(r['dirtyAt']),
    syncedAt: numberOrNull(r['syncedAt']),
    base,
    backups: Array.isArray(r['backups']) ? r['backups'].filter((k): k is string => typeof k === 'string') : [],
  };
}

/** True when it was written. A full or blocked store loses the bookkeeping, never the game. */
export function saveState(storage: KeyValueStore, state: CloudState): boolean {
  try {
    storage.setItem(STATE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}
